/**
 * Mini Cart Bundle Items
 * Renders bundle products and add-ons as separate visual items in the cart
 */

if (!customElements.get("mini-cart-bundle-items")) {
  class MiniCartBundleItems extends HTMLElement {
    constructor() {
      super();
      this.productsCache = null;
    }

    connectedCallback() {
      this.renderBundleItems();
      this.updateBundleComparePrice();
      this.setupQuantityListener();
    }

    setupQuantityListener() {
      const lineItem = this.closest('.line-item');
      if (!lineItem) return;

      const quantityInput = lineItem.querySelector('.quantity-selector__input');
      if (!quantityInput) return;

      // Listen for input changes
      quantityInput.addEventListener('input', () => {
        this.updateBundleComparePrice();
      });

      // Also listen for the theme's quantity change events
      lineItem.addEventListener('line-item-quantity:change:end', () => {
        // Wait a bit for DOM to update after cart refresh
        setTimeout(() => {
          this.updateBundleComparePrice();
        }, 100);
      });
    }

    async renderBundleItems() {
      const bundleItemsData = this.getAttribute('data-bundle-items');
      if (!bundleItemsData) return;

      try {
        const bundleItems = JSON.parse(bundleItemsData);
        if (!Array.isArray(bundleItems) || bundleItems.length === 0) return;

        // Load products data if not cached
        if (!this.productsCache) {
          await this.loadProductsData();
        }

        // Create toggle button
        const toggleButton = document.createElement('button');
        toggleButton.type = 'button';
        toggleButton.className = 'mini-cart__bundle-toggle link text--subdued text--xsmall';
        toggleButton.setAttribute('aria-expanded', 'false');
        toggleButton.textContent = 'Show details';
        
        // Create container for bundle items
        const container = document.createElement('ul');
        container.className = 'mini-cart__bundle-items list--unstyled text--subdued text--xsmall';
        container.setAttribute('role', 'list');
        container.hidden = true;

        for (const bundleItem of bundleItems) {
          const variantId = bundleItem.variantId;

          // Find variant data from cache
          const variantData = this.findVariantData(variantId);
          if (!variantData) continue;

          const listItem = document.createElement('li');
          listItem.className = 'mini-cart__bundle-item';

          const itemContent = document.createElement('div');
          itemContent.className = 'mini-cart__bundle-item-content';

          // Image - use the imageUrl extracted in findVariantData
          if (variantData.imageUrl) {
            const img = document.createElement('img');
            img.className = 'mini-cart__bundle-item-image';
            img.src = variantData.imageUrl;
            img.alt = variantData.product.title;
            img.loading = 'lazy';
            img.width = 60;
            img.height = 60;
            itemContent.appendChild(img);
          }

          // Info
          const itemInfo = document.createElement('div');
          itemInfo.className = 'mini-cart__bundle-item-info';

          const title = document.createElement('span');
          title.className = 'mini-cart__bundle-item-title';
          title.textContent = variantData.product.title;
          itemInfo.appendChild(title);

          // Variant title
          if (variantData.title && variantData.title !== 'Default Title') {
            const variantTitle = document.createElement('span');
            variantTitle.className = 'mini-cart__bundle-item-variant';
            variantTitle.textContent = variantData.title;
            itemInfo.appendChild(variantTitle);
          }

          itemContent.appendChild(itemInfo);
          listItem.appendChild(itemContent);
          container.appendChild(listItem);
        }

        // Add toggle functionality
        toggleButton.addEventListener('click', () => {
          const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
          container.hidden = isExpanded; // If expanded, hide it; if not expanded, show it
          toggleButton.setAttribute('aria-expanded', String(!isExpanded));
          toggleButton.textContent = isExpanded ? 'Show details' : 'Hide details';
        });

        // Append toggle button and container
        this.appendChild(toggleButton);
        this.appendChild(container);
      } catch (error) {
        console.error('[MiniCartBundleItems] Error rendering bundle items:', error);
      }
    }

    async loadProductsData() {
      try {
        const response = await fetch('/products.json?limit=250');
        const data = await response.json();
        this.productsCache = data.products || [];
      } catch (error) {
        console.error('[MiniCartBundleItems] Error loading products data:', error);
        this.productsCache = [];
      }
    }

    findVariantData(variantId) {
      if (!this.productsCache) return null;

      for (const product of this.productsCache) {
        const variant = product.variants.find(v => v.id === variantId);
        if (variant) {
          // Extract image URL - handle both string and object formats
          let imageUrl = null;
          if (variant.featured_image) {
            imageUrl = typeof variant.featured_image === 'string' 
              ? variant.featured_image 
              : variant.featured_image.src || variant.featured_image.url;
          } else if (variant.image) {
            imageUrl = typeof variant.image === 'string' 
              ? variant.image 
              : variant.image.src || variant.image.url;
          } else if (product.featured_image) {
            imageUrl = typeof product.featured_image === 'string' 
              ? product.featured_image 
              : product.featured_image.src || product.featured_image.url;
          }
          
          return {
            ...variant,
            product: product,
            imageUrl: imageUrl
          };
        }
      }
      return null;
    }

    formatPrice(priceInCents) {
      // Convert cents to dollars and format
      const dollars = priceInCents / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(dollars);
    }

    updateBundleComparePrice() {
      // Find the compare price element for this bundle
      const lineItem = this.closest('.line-item');
      if (!lineItem) return;

      // Find ALL compare price elements (there may be multiple price-list containers)
      const comparePriceEls = lineItem.querySelectorAll('.mini-cart__bundle-compare-price');
      if (comparePriceEls.length === 0) return;

      // Get base compare price from data attribute (set in Liquid from line item properties)
      // This is the compare price for quantity 1
      const baseComparePriceCents = comparePriceEls[0].getAttribute('data-compare-price');
      if (!baseComparePriceCents) return;

      // Get quantity from quantity selector input
      const quantityInput = lineItem.querySelector('.quantity-selector__input');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      try {
        const baseComparePrice = parseFloat(baseComparePriceCents);
        if (baseComparePrice > 0) {
          // Multiply base compare price by quantity
          const comparePrice = baseComparePrice * quantity;

          // Get the final line price to compare (use first price element found)
          const finalPriceEl = lineItem.querySelector('.price--highlight, .price');
          if (finalPriceEl) {
            // Extract price from text (remove currency symbols and parse)
            const finalPriceText = finalPriceEl.textContent.trim();
            const finalPriceMatch = finalPriceText.match(/[\d,]+\.?\d*/);
            if (finalPriceMatch) {
              const finalPrice = parseFloat(finalPriceMatch[0].replace(/,/g, '')) * 100; // Convert to cents
              
              // Only show compare price if it's greater than final price
              if (comparePrice > finalPrice) {
                const formattedComparePrice = this.formatPrice(comparePrice);
                
                // Update ALL compare price elements
                comparePriceEls.forEach(comparePriceEl => {
                  const valueEl = comparePriceEl.querySelector('.mini-cart__bundle-compare-price-value');
                  if (valueEl) {
                    valueEl.textContent = formattedComparePrice;
                    comparePriceEl.style.display = '';
                  }
                });

                // Add price--highlight class to ALL price elements in this line item
                const priceEls = lineItem.querySelectorAll('.price:not(.price--compare)');
                priceEls.forEach(priceEl => {
                  if (!priceEl.classList.contains('price--highlight')) {
                    priceEl.classList.add('price--highlight');
                  }
                });
              } else {
                // Hide compare price if it's not greater than final price
                comparePriceEls.forEach(comparePriceEl => {
                  comparePriceEl.style.display = 'none';
                });

                // Remove price--highlight class if compare price is not shown
                const priceEls = lineItem.querySelectorAll('.price:not(.price--compare)');
                priceEls.forEach(priceEl => {
                  priceEl.classList.remove('price--highlight');
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('[MiniCartBundleItems] Error updating bundle compare price:', error);
      }
    }
  }

  customElements.define("mini-cart-bundle-items", MiniCartBundleItems);
}
